import { gql } from "@apollo/client";
import { Button, Dialog, DialogContent, DialogTitle, Divider } from "@mui/material";
import Field from "controls/Field";
import Form, { FormSubmitButton } from "controls/Form";
import Message from "controls/Message";
import { GLORYLEAGUE_WEBSITE_URL } from "helpers/constants";
import useAction, { useActionFragment } from "hooks/useAction";
import useData from "hooks/useData";
import useFormDialogs from "hooks/useFormDialogs";
import useShowMessage from "hooks/useShowMessage";
import SelectInput from "inputs/SelectInput";
import TextInput from "inputs/TextInput";
import React, { useEffect, useState } from "react";

import PublicForm from "./PublicForm";
import OpenSupportCaseDialog from "./sign_up/OpenSupportDialog";

export default function SignUpPage() {
  const [orgId, orgIdSet] = useState(null);
  const [competitionId, competitionIdSet] = useState(null);
  const [leagueId, leagueIdSet] = useState(null);
  const [teamId, teamIdSet] = useState(null);
  const [role, roleSet] = useState("Player");
  const [existingSignupUuid, existingSignupUuidSet] = useState(null);
  const [signupOutcome, signupOutcomeSet] = useState(null);
  const [submittedFormData, submittedFormDataSet] = useState({});
  const showMessage = useShowMessage();

  useEffect(() => {
    if (signupOutcome) existingSignupUuidSet(signupOutcome.signupUuid);
  }, [signupOutcome]);

  useEffect(() => {
    competitionIdSet(null);
    leagueIdSet(null);
    teamIdSet(null);
  }, [orgId]);

  useEffect(() => {
    leagueIdSet(null);
    teamIdSet(null);
  }, [competitionId]);

  useEffect(() => {
    teamIdSet(null);
  }, [leagueId]);

  const queryVariables = {
    orgId,
    competitionId,
    leagueId,
    hasOrg: Boolean(orgId),
    hasCompetition: Boolean(competitionId),
    hasLeague: Boolean(leagueId),
  };

  const [data] = useData(
    gql`
      query SignUpForm(
        $orgId: ID
        $competitionId: ID
        $leagueId: ID
        $hasOrg: Boolean!
        $hasCompetition: Boolean!
        $hasLeague: Boolean!
      ) {
        allowSignUpOrgs {
          id
          name
        }
        signUpOrg(id: $orgId) @include(if: $hasOrg) {
          id
          name
          signUpCompetitions {
            id
            name
            lastPlayedAt
          }
        }
        signUpCompetition(id: $competitionId, orgId: $orgId) @include(if: $hasCompetition) {
          id
          name
          signUpLeagues {
            id
            name
            lastPlayedAt
          }
        }
        signUpLeague(id: $leagueId, orgId: $orgId) @include(if: $hasLeague) {
          id
          name
          signUpTeams {
            id
            name
          }
        }
      }
    `,
    queryVariables,
    { returnPartialData: true },
  );

  const competitions = data?.signUpOrg?.signUpCompetitions ?? [];
  const selectedCompetition = competitions.find((competition) => competition.id === competitionId);
  const leagues = data?.signUpCompetition?.signUpLeagues ?? [];
  const selectedLeague = leagues.find((league) => league.id === leagueId);
  const teams = data?.signUpLeague?.signUpTeams ?? [];
  const selectedTeam = teams.find((team) => team.id === teamId);

  const addSignup = useAction(gql`
    mutation SignUpForm($input: AddSignupInput!) {
      addSignup(input: $input) {
        signupOutcome {
          signupUuid
          name
          alreadyConnected
          differentEmail
          claimable
          obfuscatedExistingPersonEmail
        }
      }
    }
  `);

  const signupClaimUnusedAccount = useActionFragment("signupClaimUnusedAccount");
  const [formDialogs, formDialogsOpen] = useFormDialogs();

  return (
    <PublicForm
      title="Connect your Profile"
      paperProps={{
        component: Form,
        onSubmit: async (formData) => {
          const selectedTeamForSubmit = teams.find((team) => team.id === formData.teamId) ?? selectedTeam;
          submittedFormDataSet({
            orgName: data?.signUpOrg?.name,
            competitionName: selectedCompetition?.name,
            leagueName: selectedLeague?.name,
            teamName: selectedTeamForSubmit?.name,
            ...formData,
          });
          const result = await addSignup({
            input: {
              ...formData,
              existingSignupUuid,
            },
          });

          if (result.addSignup.signupOutcome.name !== "support_case") signupOutcomeSet(result.addSignup.signupOutcome);
          else {
            await showMessage({
              modal: true,
              header: <>Your request has been submitted</>,
              content: (
                <>
                  <p>We will review your request and get back to you as soon as possible.</p>
                </>
              ),
            });
            window.location.href = GLORYLEAGUE_WEBSITE_URL;
          }
        },
      }}
    >
      {formDialogs}
      <Message
        type="info"
        content={
          <>
            You must be listed on the roster of a completed game on a Glory League court to connect your account. Please
            see{" "}
            <a
              href="https://www.gloryleague.basketball/support/how-to-get-access-to-glory-league-if-you-havent-received-an-email"
              target="_blank"
              rel="noopener noreferrer"
            >
              this page
            </a>{" "}
            for more information.
          </>
        }
      />
      <Field
        name="orgId"
        value={orgId}
        onChange={orgIdSet}
        label="Organisation"
        required
        input={
          <SelectInput
            options={data?.allowSignUpOrgs?.map((org) => ({
              value: org.id,
              text: org.name,
            }))}
          />
        }
      />
      <Field
        name="competitionId"
        label="Season"
        value={competitionId}
        onChange={competitionIdSet}
        required
        input={
          <SelectInput
            options={competitions.map((competition) => ({
              value: competition.id,
              text:
                competition.name +
                (competition.lastPlayedAt ? ` (${new Date(competition.lastPlayedAt).getFullYear()})` : ""),
            }))}
          />
        }
      />
      <Field
        name="leagueId"
        label="Grade"
        value={leagueId}
        onChange={leagueIdSet}
        required
        input={
          <SelectInput
            options={leagues.map((league) => ({
              value: league.id,
              text: league.name + (league.lastPlayedAt ? ` (${new Date(league.lastPlayedAt).getFullYear()})` : ""),
            }))}
          />
        }
      />
      <Field
        name="teamId"
        label="Team"
        value={teamId}
        onChange={teamIdSet}
        required
        input={
          <SelectInput
            options={teams.map((team) => ({
              value: team.id,
              text: team.name,
            }))}
          />
        }
      />
      <Field
        name="role"
        label="Your role in the team"
        value={role}
        onChange={roleSet}
        input={<SelectInput options={["Player", "Coach"]} />}
        required
      />
      <Field name="email" label="Your Email Address" required type="email" input={<TextInput autoComplete="email" />} />
      <Field
        name="firstName"
        label="Registered Participant First Name"
        required
        input={<TextInput autoComplete="given-name" />}
      />
      <Field
        name="lastName"
        label="Registered Participant Last Name"
        required
        input={<TextInput autoComplete="family-name" />}
      />
      <Divider />
      <FormSubmitButton content="Connect Profile" />
      <Dialog scroll="body" open={!!signupOutcome} onClose={() => signupOutcomeSet(null)}>
        {signupOutcome?.name === "invalid" && (
          <>
            <DialogTitle>Not on Roster</DialogTitle>
            <DialogContent>
              <p>
                You are not in the team roster of: <br />
                <b>{submittedFormData.competitionName ?? selectedCompetition?.name}</b>
                {" - "}
                <b>{submittedFormData.leagueName ?? selectedLeague?.name}</b>
                {" - "}
                <b>{submittedFormData.teamName ?? selectedTeam?.name}</b>.
              </p>
              <br />
              <p>Your details:</p>
              <p>
                {submittedFormData.firstName} {submittedFormData.lastName} ({submittedFormData.email})
              </p>
              <br />
              <p>Please check the details are correct and use the button below to try again.</p>
              <br />
              <FormSubmitButton onClick={() => signupOutcomeSet(null)} content="Try Again" />
            </DialogContent>
          </>
        )}
        {signupOutcome?.alreadyConnected && (
          <>
            <DialogTitle>Profile already connected</DialogTitle>
            <DialogContent>
              <p>Your profile is already connected with Glory League.</p>
              <br />
              <p>
                You can <a href="/login">log in</a> to watch your game videos.
              </p>
            </DialogContent>
          </>
        )}
        {signupOutcome?.differentEmail && (
          <>
            <DialogTitle>This profile is already connected</DialogTitle>
            <DialogContent>
              <p>
                This profile is already connected to an account under a different email address: <br />
                {signupOutcome?.obfuscatedExistingPersonEmail}
              </p>
            </DialogContent>
          </>
        )}
        {signupOutcome?.claimable && (
          <>
            <DialogTitle>Connect your Profile</DialogTitle>
            <DialogContent>
              <p>We&apos;ve found an existing Glory League profile that matches your details.</p>
              <br />
              <p>Connecting your email will give you access to this profile.</p>
              <br />
              <FormSubmitButton
                onClick={async () => {
                  await signupClaimUnusedAccount({
                    input: {
                      signupUuid: signupOutcome.signupUuid,
                    },
                  });
                  await showMessage({
                    modal: true,
                    header: "Profile Connected",
                    content: (
                      <>
                        <p>Your email has been connected to your Glory League profile.</p>
                        <br />
                        <p>We&apos;ve sent you an email with next steps to access your account.</p>
                      </>
                    ),
                  });
                  window.location.href = GLORYLEAGUE_WEBSITE_URL;
                }}
                content="Connect My Profile"
              />
            </DialogContent>
          </>
        )}
        <DialogContent>
          <p>
            Need more help?
            <Button
              onClick={async () =>
                formDialogsOpen(
                  <OpenSupportCaseDialog
                    signupUuid={signupOutcome?.signupUuid}
                    submittedFormData={submittedFormData}
                  />,
                )
              }
            >
              Open a Support Case
            </Button>
          </p>
        </DialogContent>
      </Dialog>
    </PublicForm>
  );
}
